import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { MessageWithSupportTicketId } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  createDraftsCollection as createDraftsCollectionCore,
  type DraftsCollection,
} from "@redotech/redo-support-v2/drafts/support-ticket-drafts-collection";
import {
  createSupportTicketListCollection as createSupportTicketListCollectionCore,
  type SupportTicketListCollectionWithLoader,
} from "@redotech/redo-support-v2/query/support-ticket-list-collection";
import type { SupportTicketQueryScope } from "@redotech/redo-support-v2/query/support-ticket-query-keys";

import {
  createMessagesCollection as createMessagesCollectionCore,
  createThreadsCollection as createThreadsCollectionCore,
  createTimelineEventsCollection as createTimelineEventsCollectionCore,
  fetchSupportTicketMessages as fetchSupportTicketMessagesCore,
  fetchSupportTicketThreads as fetchSupportTicketThreadsCore,
  supportTicketTranscriptQueryKeys,
  type MessagesCollection,
  type SupportTicketThreadCollectionItem,
  type ThreadsCollection,
  type TimelineEventsCollection,
} from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
import {
  createPresenceCollection as createPresenceCollectionCore,
  type PresenceCollection,
} from "@redotech/redo-support-v2/realtime/support-ticket-presence-collection";

import {
  createCustomerEventsCollection as createCustomerEventsCollectionCore,
  createOrdersCollection as createOrdersCollectionCore,
  createReturnsCollection as createReturnsCollectionCore,
  type CustomerEventsCollection,
  type OrdersCollection,
  type ReturnsCollection,
} from "@redotech/redo-support-v2/query/support-ticket-related-records-collections";
import {
  createTagGroupsCollection as createTagGroupsCollectionCore,
  createTagsCollection as createTagsCollectionCore,
  type SupportTicketTagQueryScope,
  type TagGroupsCollection,
  type TagsCollection,
} from "@redotech/redo-support-v2/query/support-ticket-tags-collections";
import {
  createSupportTicketUsersCollection as createSupportTicketUsersCollectionCore,
  type SupportTicketUsersCollection,
} from "@redotech/redo-support-v2/query/support-ticket-users-collection";
import type { QueryClient } from "@tanstack/react-query";
import {
  createMerchantSupportTicketDataSource,
  type MerchantSupportTicketDataSource,
} from "./support-ticket-data-source";

export const transcriptQueryKeys: {
  readonly all: readonly ["supportTickets", "transcript"];
  readonly threads: () => readonly ["supportTickets", "transcript", "threads"];
  readonly messages: () => readonly [
    "supportTickets",
    "transcript",
    "messages",
  ];
  readonly timelineEvents: () => readonly [
    "supportTickets",
    "transcript",
    "timelineEvents",
  ];
} = supportTicketTranscriptQueryKeys;

interface CreateGlobalCollectionsOptions {
  queryClient: QueryClient;
  effectApi: EffectApiContext;
  queryScope?: SupportTicketQueryScope;
  tagQueryScope?: SupportTicketTagQueryScope;
}

export async function fetchSupportTicketThreads(
  effectApi: EffectApiContext,
  supportTicketId: SupportTicketId,
): Promise<SupportTicketThreadCollectionItem[]> {
  return fetchSupportTicketThreadsCore(
    createMerchantSupportTicketDataSource({ effectApi }).transcript,
    supportTicketId,
  );
}

export async function fetchSupportTicketMessages(
  effectApi: EffectApiContext,
  supportTicketId: SupportTicketId,
): Promise<MessageWithSupportTicketId[]> {
  return fetchSupportTicketMessagesCore(
    createMerchantSupportTicketDataSource({ effectApi }).transcript,
    supportTicketId,
  );
}

/**
 * Pull a ticket's authoritative transcript from the server and reconcile it
 * into the on-demand collections so a merge shows up live — even for threads
 * (and their messages) that were already loaded under a now-merged source
 * ticket earlier this session.
 *
 * The server returns every thread/message under its current ticket, so the
 * fetched rows all carry `supportTicketId === supportTicketId`, including moved
 * ones. A row already cached under a DIFFERENT ticket id is mis-indexed: the
 * collections autoIndex on `supportTicketId`, and BasicIndex does not move a row
 * when an upsert only changes that field, so the `eq(supportTicketId, …)` live
 * query keeps missing it. We delete those rows first so the upsert re-inserts
 * them with a correct index. Rows already under the right id are updated in
 * place, so a transcript that is already correct never flashes.
 */
export async function reconcileSupportTicketTranscript({
  effectApi,
  threads: threadsCollection,
  messages: messagesCollection,
  supportTicketId,
  signal,
}: {
  effectApi: EffectApiContext;
  threads: ThreadsCollection;
  messages: MessagesCollection;
  supportTicketId: SupportTicketId;
  signal?: AbortSignal;
}): Promise<void> {
  const [threads, messages] = await Promise.all([
    fetchSupportTicketThreads(effectApi, supportTicketId),
    fetchSupportTicketMessages(effectApi, supportTicketId),
  ]);
  if (signal?.aborted) {
    return;
  }

  if (threadsCollection.isReady()) {
    const staleKeys: string[] = [];
    for (const thread of threads) {
      const key = thread.id.toString();
      const existing = threadsCollection.get(key);
      if (
        existing !== undefined &&
        existing.supportTicketId !== thread.supportTicketId
      ) {
        staleKeys.push(key);
      }
    }
    if (staleKeys.length > 0) {
      threadsCollection.utils.writeBatch(() => {
        for (const key of staleKeys) {
          threadsCollection.utils.writeDelete(key);
        }
      });
    }
    threadsCollection.utils.writeBatch(() => {
      for (const thread of threads) {
        threadsCollection.utils.writeUpsert(thread);
      }
    });
  }

  if (messagesCollection.isReady()) {
    const staleKeys: string[] = [];
    for (const message of messages) {
      const key = message.id.toString();
      const existing = messagesCollection.get(key);
      if (
        existing !== undefined &&
        existing.supportTicketId !== message.supportTicketId
      ) {
        staleKeys.push(key);
      }
    }
    if (staleKeys.length > 0) {
      messagesCollection.utils.writeBatch(() => {
        for (const key of staleKeys) {
          messagesCollection.utils.writeDelete(key);
        }
      });
    }
    messagesCollection.utils.writeBatch(() => {
      for (const message of messages) {
        messagesCollection.utils.writeUpsert(message);
      }
    });
  }
}

function createThreadsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createThreadsCollectionCore({
    queryClient,
    api: dataSource.transcript,
    scope,
  });
}

function createMessagesCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createMessagesCollectionCore({
    queryClient,
    api: dataSource.transcript,
    scope,
  });
}

function createTimelineEventsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createTimelineEventsCollectionCore({
    queryClient,
    api: dataSource.transcript,
    scope,
  });
}

function createPresenceCollection(queryClient: QueryClient) {
  return createPresenceCollectionCore({ queryClient });
}

function createTagsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketTagQueryScope,
) {
  return createTagsCollectionCore({ queryClient, api: dataSource.tags, scope });
}

function createTagGroupsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketTagQueryScope,
) {
  return createTagGroupsCollectionCore({
    queryClient,
    api: dataSource.tags,
    scope,
  });
}

function createSupportTicketUsersCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
) {
  return createSupportTicketUsersCollectionCore({
    queryClient,
    api: dataSource.users,
  });
}

function createOrdersCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createOrdersCollectionCore({
    queryClient,
    api: dataSource.relatedRecords,
    scope,
  });
}

function createReturnsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createReturnsCollectionCore({
    queryClient,
    api: dataSource.relatedRecords,
    scope,
  });
}

function createCustomerEventsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createCustomerEventsCollectionCore({
    queryClient,
    api: dataSource.relatedRecords,
    scope,
  });
}

function createDraftsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
) {
  return createDraftsCollectionCore({ queryClient, api: dataSource.drafts });
}

export type SupportTicketListCollection = SupportTicketListCollectionWithLoader;

export interface GlobalSupportTicketCollections {
  supportTicketsList: SupportTicketListCollection;
  threads: ThreadsCollection;
  messages: MessagesCollection;
  drafts: DraftsCollection;
  presence: PresenceCollection;
  tags: TagsCollection;
  tagGroups: TagGroupsCollection;
  users: SupportTicketUsersCollection;
  orders: OrdersCollection;
  returns: ReturnsCollection;
  timelineEvents: TimelineEventsCollection;
  customerEvents: CustomerEventsCollection;
}

export function createGlobalSupportTicketCollections(
  options: CreateGlobalCollectionsOptions,
): GlobalSupportTicketCollections {
  const dataSource = createMerchantSupportTicketDataSource(options);
  const supportTicketsList = createSupportTicketListCollectionCore({
    queryClient: options.queryClient,
    api: dataSource.list,
    scope: options.queryScope,
    onUpdateError: (supportTicketId, err) => {
      console.error("[supportTickets] PATCH failed for", supportTicketId, err);
    },
  });
  const threads = createThreadsCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  const messages = createMessagesCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  const drafts = createDraftsCollection(options.queryClient, dataSource);
  const presence = createPresenceCollection(options.queryClient);
  const tags = createTagsCollection(
    options.queryClient,
    dataSource,
    options.tagQueryScope,
  );
  const tagGroups = createTagGroupsCollection(
    options.queryClient,
    dataSource,
    options.tagQueryScope,
  );
  const users = createSupportTicketUsersCollection(
    options.queryClient,
    dataSource,
  );
  const orders = createOrdersCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  const returns = createReturnsCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  const customerEvents = createCustomerEventsCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  const timelineEvents = createTimelineEventsCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  return {
    supportTicketsList,
    threads,
    messages,
    drafts,
    presence,
    tags,
    tagGroups,
    users,
    orders,
    returns,
    timelineEvents,
    customerEvents,
  };
}

export { type DraftCollectionItem } from "@redotech/redo-support-v2/drafts/support-ticket-draft-requests";
export { supportTicketDraftQueryKeys as draftQueryKeys } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
export {
  supportTicketOrderQueryKeys as orderQueryKeys,
  supportTicketReturnQueryKeys as returnQueryKeys,
  type OrderWithPickStatus,
} from "@redotech/redo-support-v2/query/support-ticket-related-records-collections";
export { supportTicketTagQueryKeys as tagQueryKeys } from "@redotech/redo-support-v2/query/support-ticket-tags-collections";
export {
  supportTicketPresenceQueryKeys as presenceQueryKeys,
  type SupportTicketPresenceRecord,
} from "@redotech/redo-support-v2/realtime/support-ticket-presence-collection";

export { supportTicketTranscriptQueryKeys as supportTicketTranscriptKeys } from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
